// Sopwith 3D - browser entry point.
//
// Title screen -> game -> game over -> title. The simulation ticks at 10 Hz
// from keyboard and gamepad input; the three.js world is the default view
// and `?view=classic` shows the original-style 2D view.

import originalSop from '../assets/levels/original.sop?raw';
import { Audio } from './audio/audio';
import { parseTune, type TuneNote } from './audio/tune';
import { HighScores } from './hud/highscores';
import { Hud } from './hud/hud';
import { TitleScreen } from './hud/title';
import { GamepadInput } from './input/gamepad';
import { Keyboard } from './input/keyboard';
import { isTouchDevice, TouchInput } from './input/touch';
import { ClassicCanvas } from './render/debug2d';
import { PALETTES } from './render/framebuffer';
import { WorldRenderer } from './render/world';
import {
  FPS,
  Game,
  type GameConfig,
  type Mission,
  ObjState,
  parseMission,
  PlayMode,
} from './sim';

window.addEventListener('error', (e) => {
  console.error('[sopwith] uncaught:', e.message, e.error?.stack);
});

const params = new URLSearchParams(location.search);
const MODES: Record<string, PlayMode> = {
  novice: PlayMode.NOVICE,
  single: PlayMode.SINGLE,
  computer: PlayMode.COMPUTER,
};
const MODE_NAMES: Record<number, string> = {
  [PlayMode.NOVICE]: 'novice',
  [PlayMode.SINGLE]: 'solo',
  [PlayMode.COMPUTER]: 'dogfight',
};
const baseMission = parseMission(originalSop);
let mission: Mission = baseMission;
let missionNote = '';

/** Load a custom .sop mission from ?file=URL, falling back to the original. */
async function loadCustomMission(): Promise<void> {
  const url = params.get('file');
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const custom = parseMission(await res.text());
    if (!custom.level && !custom.symbols && !custom.sounds) {
      throw new Error('no level, symbols or sounds section');
    }
    mission = {
      level: custom.level ?? baseMission.level,
      symbols: custom.symbols
        ? new Map([...(baseMission.symbols ?? []), ...custom.symbols])
        : baseMission.symbols,
      sounds: custom.sounds ?? baseMission.sounds,
      title: custom.title ?? baseMission.title,
    };
    missionNote = `Custom mission: ${url.split('/').pop() ?? url}`;
  } catch (err) {
    missionNote = `Could not load ${url}: ${(err as Error).message}. Playing the original.`;
    console.warn('[sopwith]', missionNote);
  }
}

function titleTune(m: Mission): TuneNote[] {
  const prop = m.sounds?.props.find((p) => p.name === 'title_tune');
  return prop && typeof prop.value === 'string' ? parseTune(prop.value) : [];
}

function config(mode: PlayMode): Partial<GameConfig> {
  return {
    playmode: mode,
    startingLevel: Number(params.get('level') ?? 0) || 0,
    screenPitch: !params.has('pilotkeys'),
    autoRoll: !params.has('pilotkeys'),
    missiles: params.has('missiles'),
    wounded: params.has('wounded'),
    seed: (Date.now() & 0xffff) || 7491,
  };
}

await loadCustomMission();

let game = new Game(mission, config(MODES[params.get('mode') ?? ''] ?? PlayMode.COMPUTER));
(window as unknown as { sopwith: { game: Game } }).sopwith = { game };

const app = document.getElementById('app')!;
const keyboard = new Keyboard();
const gamepad = new GamepadInput();
const touch = isTouchDevice() || params.has('touch') ? new TouchInput(app) : null;
const audio = new Audio();
const tune = titleTune(mission);
const highs = new HighScores(typeof localStorage !== 'undefined' ? localStorage : null);

if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  // When a newer worker takes over an already-controlled page, reload once
  // so the player gets the new build instead of the cached one.
  const controlled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (controlled) location.reload();
  });
  navigator.serviceWorker.register('./sw.js').catch(() => {});
}

interface View {
  bind(game: Game): void;
  commit(): void;
  render(alpha: number, dt: number): void;
  renderAttract(dt: number): void;
  onLevelStart(): void;
  paletteName(): string;
  cyclePalette(): void;
  /** Left and right world X currently visible, for the minimap. */
  visibleSpan(): [number, number];
  setVisible(v: boolean): void;
}

function classicView(): View {
  const v = new ClassicCanvas(app, {
    palette: Number(params.get('palette') ?? 0) || 0,
    solidGround: params.has('solid'),
  });
  return {
    bind: () => {},
    commit: () => {},
    render: () => v.render(game),
    renderAttract: () => v.render(game),
    onLevelStart: () => {},
    paletteName: () => v.palette.name,
    cyclePalette: () => {
      v.palette = PALETTES[(PALETTES.indexOf(v.palette) + 1) % PALETTES.length];
    },
    visibleSpan: () => [v.view.displx, v.view.displx + 320],
    setVisible: (on) => {
      v.canvas.hidden = !on;
    },
  };
}

function worldView(): View {
  const w = new WorldRenderer(app, game);
  return {
    bind: (g) => w.bind(g),
    commit: () => w.commit(),
    render: (alpha, dt) => w.render(alpha, dt),
    renderAttract: (dt) => w.renderAttract(dt),
    onLevelStart: () => w.snapCamera(),
    paletteName: () => '3D',
    cyclePalette: () => {},
    visibleSpan: () => {
      const cx = w.camera.camera.position.x;
      const hw = w.camera.halfWidth;
      return [cx - hw, cx + hw];
    },
    setVisible: (on) => {
      w.renderer.domElement.hidden = !on;
    },
  };
}

// Both views exist; retro mode shows the classic one. The preference is
// remembered, and ?view=classic forces it on for the session.
const RETRO_KEY = 'sopwith3d.retro';
let retro = params.get('view') === 'classic' || localStorage.getItem(RETRO_KEY) === '1';
const world = worldView();
const classicV = classicView();
let view: View = retro ? classicV : world;
const hud = new Hud(app);
const title = new TitleScreen(app, highs);

function setRetro(on: boolean): void {
  retro = on;
  try {
    localStorage.setItem(RETRO_KEY, on ? '1' : '0');
  } catch {
    // storage unavailable
  }
  view = on ? classicV : world;
  world.setVisible(!on);
  classicV.setVisible(on);
  hud.setRetro(on);
  title.retro = on;
  if (!on) world.onLevelStart();
}
setRetro(retro);
title.onRetro = setRetro;

let playing = false;

function startGame(mode: PlayMode): void {
  audio.unlock();
  audio.stopTune();
  game = new Game(mission, config(mode));
  (window as unknown as { sopwith: { game: Game } }).sopwith = { game };
  world.bind(game);
  game.takeEvents();
  title.hide();
  hud.setVisible(true);
  touch?.setVisible(true);
  playing = true;
  accumulator = 0;
}

function endGame(): void {
  playing = false;
  const p = game.consoleplayer;
  const rank = highs.add({
    score: p.score.score,
    level: game.gamenum,
    mode: MODE_NAMES[game.playmode] ?? 'solo',
    date: new Date().toISOString().slice(0, 10),
    medals: p.score.medals.length,
  });
  hud.setVisible(false);
  touch?.setVisible(false);
  title.showGameOver(p.score, game.gamenum, game.playmode, rank);
}

title.onStart = ({ mode }) => startGame(mode);
title.note = missionNote;
title.showTitle();
hud.setVisible(false);
touch?.setVisible(false);

// Browsers only allow audio after a gesture: unlock on the first one and
// start the title tune if we are still on the title.
const unlockAudio = (): void => {
  audio.unlock();
  if (!playing && !audio.tunePlaying) audio.playTune(tune);
};
app.addEventListener('pointerdown', unlockAudio);
window.addEventListener('keydown', unlockAudio);

// P cycles the monitor palette (classic view); F1 toggles the debug readout;
// S toggles sound.
const debug = document.getElementById('debug')!;
let showDebug = params.has('debug');
window.addEventListener('keydown', (e) => {
  if (e.code === 'KeyP') {
    view.cyclePalette();
  } else if (e.code === 'F1') {
    showDebug = !showDebug;
    e.preventDefault();
  } else if (e.code === 'KeyS' && !e.repeat) {
    audio.unlock();
    hud.flashMessage(audio.toggle() ? 'SOUND ON' : 'SOUND OFF');
  } else if (e.code === 'KeyR' && !e.repeat) {
    setRetro(!retro);
    hud.flashMessage(retro ? 'RETRO MODE' : '3D MODE');
  }
  if (e.code === 'Escape' && playing) {
    game.end();
  }
});

const TICK_MS = 1000 / FPS;
let accumulator = 0;
let last = performance.now();
let fps = 0;

let lastErrorAt = 0;

function frame(now: number): void {
  requestAnimationFrame(frame);
  try {
    step(now);
  } catch (err) {
    // Keep the loop alive; report at most once a second.
    if (now - lastErrorAt > 1000) {
      lastErrorAt = now;
      console.error('[sopwith] frame error:', err);
    }
  }
}

function step(now: number): void {
  const dt = Math.min(now - last, 250);
  last = now;

  if (playing) {
    accumulator += dt;
    while (accumulator >= TICK_MS) {
      game.tick(keyboard.poll() | gamepad.poll() | (touch?.poll() ?? 0));
      world.commit(); // keeps interpolation continuous across a retro switch
      accumulator -= TICK_MS;
    }
    for (const ev of game.takeEvents()) {
      if (ev.kind === 'levelStart') world.onLevelStart();
      if (ev.kind === 'levelComplete') {
        hud.flashMessage(`LEVEL ${ev.level + 1} CLEARED  +${ev.bonus}`, 3000);
      }
    }
    view.render(accumulator / TICK_MS, dt / 1000);
    hud.update(game);
    hud.drawMinimap(game, ...view.visibleSpan());
    audio.update(game);
    for (const ev of game.collisionEvents) {
      hud.onCollision(ev.kind);
    }
    if (game.isOver) {
      endGame();
      if (audio.ready) audio.playTune(tune);
    }
  } else {
    view.renderAttract(dt / 1000);
  }

  fps = fps * 0.95 + (1000 / Math.max(dt, 1)) * 0.05;
  const p = game.consoleplayer;
  debug.hidden = !showDebug;
  if (showDebug) {
    debug.textContent = [
      `tick ${game.countmove}  level ${game.gamenum}  view ${view.paletteName()}  ${fps.toFixed(0)} fps`,
      `state ${ObjState[p.state]}  home ${p.athome}  pad ${gamepad.connected}`,
      `x ${p.x}  y ${p.y}  angle ${p.angle}  orient ${p.orient}`,
      `speed ${p.speed}  throttle ${p.accel}`,
      `fuel ${p.life}  rounds ${p.rounds}  bombs ${p.bombs}  lives ${game.maxcrash - p.crashcnt}`,
      `kills ${p.score.planekills}+${p.flightscore.planekills}  valour ${p.score.valour}  medals ${p.score.medals}  ribbons ${p.score.ribbons}`,
    ].join('\n');
  }
}
requestAnimationFrame(frame);
